import React, { Component } from 'react';
import _AdminService from 'modules/AdminService';
import _InterviewScheduleService from 'modules/InterviewScheduleService';
import _notification from 'modules/notification';
import { Card, Table, Modal, Tag, DatePicker } from 'antd';
import EmptyState from 'components/enterprise/EmptyState';
import LoadingSpinner from 'components/enterprise/LoadingSpinner';
import PageHeader from 'components/enterprise/PageHeader';
import DropdownList from 'components/controls/DropdownList';
import { routesAdmin } from 'routes/Routes';
import ConfirmPopup from '../popup/ConfirmPopup';
import ButtonComponent from 'components/controls/Button';
import FileUpload from 'components/controls/FileUpload';
import { GENERAL_ERROR_MESSAGE } from 'constants/constants';
import moment from 'moment';

// Phase B (Interview Scheduling) — admin Excel import (Select -> Validate -> Review -> Confirm) plus a
// filterable schedule list with the Draft/Released/Archived lifecycle, mirroring the Publication/
// Answer Key admin conventions already established. Restricted to candidates already marked interview-
// eligible (StudentResult.IsInterviewShortlisted) by the existing Primary Result mechanism — this
// screen never computes or overrides that eligibility itself.
const STATUS_TAG_COLOR = { Draft: undefined, Released: 'green', Archived: 'orange' };
const STATUS_OPTIONS = ['Draft', 'Released', 'Archived'];

class InterviewScheduleMaster_new extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            posts: [],
            schedules: [],
            // Import wizard state
            importPostId: null,
            selectedFile: null,
            fileError: '',
            validating: false,
            importing: false,
            validationSummary: null,
            // List filters
            filterPostId: null,
            filterStatus: null,
            filterDate: null,
            // Pagination Consistency phase (28 Aug 2026): was a hardcoded `pagination={{ pageSize: 10 }}`
            // literal recreated fresh on every render (not backed by state, no `onChange` wired to the
            // Table) — every sibling admin list page (e.g. PostMaster.jsx) instead tracks pagination in
            // state and feeds the Table's own onChange back into it, the standard antd controlled-
            // pagination pattern. Matched here for consistency and to remove the risk of the current
            // page silently resetting whenever this component re-renders for an unrelated reason (e.g.
            // a filter change) while a fresh, `current`-less pagination object is passed back down.
            pagination: { pageSize: 10, current: 1 },
            // Edit modal
            showEditModal: false,
            editing: null,
            editInterviewDate: null,
            editInterviewTime: '',
            editVenue: '',
            saving: false,
            // Archive confirm
            showArchiveConfirm: false,
            archiveTargetId: null,
            // Phase C (Interview Call Letter)
            callLetterPostId: null,
            callLetterSummary: null,
            callLetterChecking: false,
            callLetterActing: false,
            showPreviewModal: false,
            previewData: null,
            previewLoading: false
        };
    }

    componentDidMount() {
        this.loadPosts();
        this.search();
    }

    loadPosts = async () => {
        try {
            const response = await _AdminService.GetAllPost();
            this.setState({ posts: response.data || [], loading: false });
        } catch (e) {
            this.setState({ loading: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    search = async () => {
        try {
            const dateParam = this.state.filterDate ? this.state.filterDate.format('YYYY-MM-DD') : null;
            const response = await _InterviewScheduleService.search(this.state.filterPostId, this.state.filterStatus, dateParam);
            this.setState({ schedules: response.data || [] });
        } catch (e) {
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    onImportPostChange = (event) => {
        const postId = event.target.value ? parseInt(event.target.value, 10) : null;
        this.setState({ importPostId: postId, selectedFile: null, fileError: '', validationSummary: null });
    }

    onFileDrop = (file) => {
        this.setState({ selectedFile: file, fileError: '', validationSummary: null });
    }

    onFileError = (message) => {
        this.setState({ fileError: message, selectedFile: null });
    }

    handleValidate = async () => {
        if (!this.state.importPostId) {
            _notification.showMessage('error', 'Please select a Post first.');
            return;
        }
        if (!this.state.selectedFile) {
            _notification.showMessage('error', 'Please select a file to upload.');
            return;
        }
        this.setState({ validating: true });
        try {
            const response = await _InterviewScheduleService.validateImport(this.state.importPostId, this.state.selectedFile);
            if (response.status === 200) {
                this.setState({ validating: false, validationSummary: response.data });
            } else {
                this.setState({ validating: false });
                _notification.showMessage('error', (response.data && response.data.Message) || response.data || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ validating: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    handleConfirmImport = async () => {
        this.setState({ importing: true });
        try {
            const response = await _InterviewScheduleService.confirmImport(this.state.importPostId, this.state.selectedFile);
            if (response.status === 200) {
                _notification.showMessage('success', `Import complete. ${response.data.PersistedCount} schedule(s) staged as Draft — release them from the list below when ready.`);
                this.setState({ importing: false, selectedFile: null, validationSummary: null, importPostId: null });
                this.search();
            } else {
                this.setState({ importing: false });
                _notification.showMessage('error', (response.data && response.data.Message) || response.data || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ importing: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    onFilterPostChange = (event) => {
        const postId = event.target.value ? parseInt(event.target.value, 10) : null;
        this.setState({ filterPostId: postId }, this.search);
    }

    onFilterStatusChange = (event) => {
        this.setState({ filterStatus: event.target.value || null }, this.search);
    }

    onFilterDateChange = (date) => {
        this.setState({ filterDate: date }, this.search);
    }

    // Pagination Consistency phase: same controlled-pagination pattern as PostMaster.jsx's
    // onTableAction — persists whatever antd's Table onChange hands back (current page, page size,
    // sorter) into state, which then flows back down as the `pagination` prop.
    onTableAction = (pagination) => {
        this.setState({ pagination });
    }

    handleRelease = async (record) => {
        try {
            const response = await _InterviewScheduleService.release(record.Id);
            if (response.status === 200) {
                _notification.showMessage('success', 'Schedule released to the candidate.');
                this.search();
            } else {
                _notification.showMessage('error', (response.data && response.data.Message) || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    handleUnrelease = async (record) => {
        try {
            const response = await _InterviewScheduleService.unrelease(record.Id);
            if (response.status === 200) {
                _notification.showMessage('success', 'Schedule unreleased (hidden from the candidate).');
                this.search();
            } else {
                _notification.showMessage('error', (response.data && response.data.Message) || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    onArchiveClick = (record) => {
        this.setState({ showArchiveConfirm: true, archiveTargetId: record.Id });
    }

    onConfirmArchive = async () => {
        const id = this.state.archiveTargetId;
        this.setState({ showArchiveConfirm: false, archiveTargetId: null });
        try {
            const response = await _InterviewScheduleService.archive(id);
            if (response.status === 200) {
                _notification.showMessage('success', 'Schedule archived.');
                this.search();
            } else {
                _notification.showMessage('error', (response.data && response.data.Message) || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    openEditModal = (record) => {
        this.setState({
            showEditModal: true,
            editing: record,
            editInterviewDate: moment(record.InterviewDate),
            editInterviewTime: record.InterviewTime,
            editVenue: record.Venue || ''
        });
    }

    closeEditModal = () => {
        this.setState({ showEditModal: false, editing: null, editInterviewDate: null, editInterviewTime: '', editVenue: '' });
    }

    handleSaveEdit = async () => {
        const { editing, editInterviewDate, editInterviewTime, editVenue } = this.state;
        if (!editInterviewDate || !editInterviewTime.trim()) {
            _notification.showMessage('error', 'Interview Date and Time are required.');
            return;
        }
        this.setState({ saving: true });
        try {
            const response = await _InterviewScheduleService.update({
                Id: editing.Id,
                InterviewDate: editInterviewDate.format('YYYY-MM-DDT00:00:00'),
                InterviewTime: editInterviewTime.trim(),
                Venue: editVenue.trim()
            });
            if (response.status === 200) {
                _notification.showMessage('success', 'Schedule updated.');
                this.setState({ saving: false });
                this.closeEditModal();
                this.search();
            } else {
                this.setState({ saving: false });
                _notification.showMessage('error', (response.data && response.data.Message) || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ saving: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    // ==================== Phase C (Interview Call Letter) ====================

    onCallLetterPostChange = (event) => {
        const postId = event.target.value ? parseInt(event.target.value, 10) : null;
        this.setState({ callLetterPostId: postId, callLetterSummary: null }, () => {
            if (postId) this.checkCallLetterStatus();
        });
    }

    checkCallLetterStatus = async () => {
        if (!this.state.callLetterPostId) return;
        this.setState({ callLetterChecking: true });
        try {
            const response = await _InterviewScheduleService.validateCallLetterRelease(this.state.callLetterPostId);
            if (response.status === 200) {
                this.setState({ callLetterChecking: false, callLetterSummary: response.data });
            } else {
                this.setState({ callLetterChecking: false });
                _notification.showMessage('error', (response.data && response.data.Message) || response.data || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ callLetterChecking: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    handlePreviewCallLetter = async () => {
        if (!this.state.callLetterPostId) return;
        this.setState({ previewLoading: true, showPreviewModal: true });
        try {
            const response = await _InterviewScheduleService.previewCallLetter(this.state.callLetterPostId);
            if (response.status === 200) {
                this.setState({ previewLoading: false, previewData: response.data });
            } else {
                this.setState({ previewLoading: false, showPreviewModal: false });
                _notification.showMessage('error', (response.data && response.data.Message) || response.data || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ previewLoading: false, showPreviewModal: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    closePreviewModal = () => {
        this.setState({ showPreviewModal: false, previewData: null });
    }

    handleReleaseCallLetters = async () => {
        this.setState({ callLetterActing: true });
        try {
            const response = await _InterviewScheduleService.releaseCallLetters(this.state.callLetterPostId);
            if (response.status === 200) {
                _notification.showMessage('success', `Call Letters released for ${response.data.ReleasedCount} candidate(s).`);
                this.setState({ callLetterActing: false, callLetterSummary: response.data });
            } else {
                this.setState({ callLetterActing: false });
                _notification.showMessage('error', (response.data && response.data.Message) || response.data || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ callLetterActing: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    handleUnreleaseCallLetters = async () => {
        this.setState({ callLetterActing: true });
        try {
            const response = await _InterviewScheduleService.unreleaseCallLetters(this.state.callLetterPostId);
            if (response.status === 200) {
                _notification.showMessage('success', 'Call Letters unreleased (hidden from candidates).');
                this.setState({ callLetterActing: false });
                this.checkCallLetterStatus();
            } else {
                this.setState({ callLetterActing: false });
                _notification.showMessage('error', (response.data && response.data.Message) || response.data || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ callLetterActing: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    renderCallLetterSection() {
        const { posts, callLetterPostId, callLetterSummary, callLetterChecking, callLetterActing } = this.state;
        return (
            <Card className="mt-3">
                <h5 className="mb-1">Interview Call Letter</h5>
                <p style={{ fontSize: 13, color: '#666' }} className="mb-3">
                    Call Letter content comes from this Post's Interview Call Letter Format (Post Master), merged with
                    each candidate's own Released Interview Schedule (Date/Time/Venue). Releasing the Call Letter is a
                    separate, explicit step from releasing the Interview Schedule itself.
                </p>
                <div className="row">
                    <div className="col-sm-6 mb-2">
                        <DropdownList
                            value={callLetterPostId || 0}
                            name="callLetterPostId"
                            onChange={this.onCallLetterPostChange}
                            placeholder="Select Post"
                            heading="Post/Exam"
                            options={posts}
                        />
                    </div>
                    <div className="col-sm-6 mb-2 d-flex align-items-end">
                        <a href={routesAdmin.post.path} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>
                            Configure Letter Content in Post Master (Interview Call Letter Format) →
                        </a>
                    </div>
                </div>

                {callLetterChecking && <LoadingSpinner />}

                {!callLetterChecking && callLetterSummary && (
                    <div style={{ background: '#f5f7fa', padding: '14px 18px', borderRadius: 4 }}>
                        {!callLetterSummary.TemplateConfigured && (
                            <p style={{ color: '#c62828' }}>
                                No Call Letter content is configured for <b>{callLetterSummary.PostName}</b> yet.
                                Configure it in Post Master first.
                            </p>
                        )}
                        {callLetterSummary.TemplateConfigured && (
                            <>
                                <p style={{ fontSize: 13 }}>
                                    Scheduled &amp; Released candidates: <b>{callLetterSummary.ScheduledCandidateCount}</b> &nbsp;·&nbsp;
                                    Call Letter already released: <b style={{ color: '#2e7d32' }}>{callLetterSummary.AlreadyReleasedCount}</b> &nbsp;·&nbsp;
                                    Ready to release: <b>{callLetterSummary.ReadyCount}</b> &nbsp;·&nbsp;
                                    Missing Venue{callLetterSummary.RequiresVenue ? '' : ' (not required by this template)'}: <b style={{ color: callLetterSummary.MissingVenueCount ? '#c62828' : undefined }}>{callLetterSummary.MissingVenueCount}</b>
                                </p>
                                {callLetterSummary.UnresolvedPlaceholders && callLetterSummary.UnresolvedPlaceholders.length > 0 && (
                                    <p style={{ color: '#c62828', fontSize: 13 }}>
                                        Unresolved placeholder(s) in template: {callLetterSummary.UnresolvedPlaceholders.join(', ')} — fix these in Post Master before releasing.
                                    </p>
                                )}
                                {typeof callLetterSummary.ReleasedCount === 'number' && callLetterSummary.ReleasedCount > 0 && (
                                    <p style={{ color: '#2e7d32', fontSize: 13 }}>Just released: {callLetterSummary.ReleasedCount} candidate(s).</p>
                                )}
                                <div className="mt-2">
                                    <ButtonComponent type="button" value="Preview Sample Letter"
                                        onClick={this.handlePreviewCallLetter}
                                        className="button-global button-border small autoHeight mr-2 mb-1" />
                                    <ButtonComponent type="button"
                                        value={callLetterActing ? 'Releasing…' : `Release Call Letters (${callLetterSummary.ReadyCount})`}
                                        onClick={this.handleReleaseCallLetters}
                                        disabled={callLetterActing || callLetterSummary.ReadyCount === 0 || (callLetterSummary.UnresolvedPlaceholders && callLetterSummary.UnresolvedPlaceholders.length > 0)}
                                        className="button-global button-primary small autoHeight mr-2 mb-1" />
                                    {callLetterSummary.AlreadyReleasedCount > 0 && (
                                        <ButtonComponent type="button"
                                            value={callLetterActing ? 'Working…' : 'Unrelease All'}
                                            onClick={this.handleUnreleaseCallLetters}
                                            disabled={callLetterActing}
                                            className="button-global button-danger small autoHeight mb-1" />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </Card>
        );
    }

    renderValidationSummary() {
        const s = this.state.validationSummary;
        if (!s) return null;
        const invalidRows = (s.Rows || []).filter(r => !r.IsValid);
        return (
            <div className="mt-3" style={{ background: '#f5f7fa', padding: '14px 18px', borderRadius: 4 }}>
                <p className="mb-2"><b>Validation Summary for {s.PostName}</b></p>
                <p style={{ fontSize: 13 }}>
                    Total Rows: <b>{s.TotalRows}</b> &nbsp;·&nbsp;
                    Valid: <b style={{ color: '#2e7d32' }}>{s.ValidRowCount}</b> &nbsp;·&nbsp;
                    Invalid: <b style={{ color: '#c62828' }}>{s.InvalidRowCount}</b> &nbsp;·&nbsp;
                    Duplicate in file: {s.DuplicateRowCount} &nbsp;·&nbsp;
                    Already scheduled (conflict): {s.ConflictRowCount} &nbsp;·&nbsp;
                    Unknown Roll Number: {s.UnknownRollNumberCount} &nbsp;·&nbsp;
                    Not interview-eligible: {s.IneligibleCount} &nbsp;·&nbsp;
                    Post mismatch: {s.PostMismatchCount} &nbsp;·&nbsp;
                    Date/Time errors: {s.DateTimeErrorCount}
                </p>
                {invalidRows.length > 0 && (
                    <div className="table-responsive mt-2">
                        <table className="table table-sm">
                            <thead><tr><th>Row</th><th>Roll Number</th><th>Reason(s)</th></tr></thead>
                            <tbody>
                                {invalidRows.map((r, i) => (
                                    <tr key={i}>
                                        <td>{r.ExcelRowNumber}</td>
                                        <td>{r.RollNumber || '-'}</td>
                                        <td className="color-red">{(r.Errors || []).join('; ')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <ButtonComponent
                    type="button"
                    value={this.state.importing ? 'Importing…' : `Confirm Import (${s.ValidRowCount} row(s) as Draft)`}
                    onClick={this.handleConfirmImport}
                    className="button-global button-primary autoHeight small mt-2"
                    disabled={this.state.importing || s.ValidRowCount === 0}
                />
            </div>
        );
    }

    render() {
        const { posts, schedules, importPostId, fileError, validating } = this.state;

        if (this.state.loading) return <LoadingSpinner />

        const TableColumns = [
            { title: 'Roll Number', dataIndex: 'RollNumber', key: 'RollNumber', width: '9%' },
            { title: 'Candidate', dataIndex: 'StudentName', key: 'StudentName', width: '14%' },
            { title: 'Post', dataIndex: 'PostName', key: 'PostName', width: '14%' },
            {
                title: 'Interview Date', dataIndex: 'InterviewDate', key: 'InterviewDate', width: '11%',
                render: (v) => moment(v).format('DD-MMM-YYYY')
            },
            { title: 'Interview Time', dataIndex: 'InterviewTime', key: 'InterviewTime', width: '13%' },
            {
                title: 'Status', dataIndex: 'Status', key: 'Status', width: '8%',
                render: (value, record) => (
                    <>
                        <Tag color={STATUS_TAG_COLOR[value]}>{value}</Tag>
                        {value === 'Released' && !record.IsActive && <Tag color="red">Hidden</Tag>}
                    </>
                )
            },
            {
                title: 'Updated', dataIndex: 'UpdatedAt', key: 'UpdatedAt', width: '10%',
                render: (v) => v ? moment(v).format('DD-MMM-YYYY hh:mm A') : '-'
            },
            {
                title: 'Action', dataIndex: '', key: 'Id', width: '21%',
                render: (record) => (
                    <>
                        {record.Status !== 'Archived' && (
                            <ButtonComponent type="button" value="Edit" className="button-global button-border small autoHeight mr-2 mb-1" onClick={() => this.openEditModal(record)} />
                        )}
                        {record.Status !== 'Released' && record.Status !== 'Archived' && (
                            <ButtonComponent type="button" value="Release" className="button-global button-primary small autoHeight mr-2 mb-1" onClick={() => this.handleRelease(record)} />
                        )}
                        {record.Status === 'Released' && record.IsActive && (
                            <ButtonComponent type="button" value="Unrelease" className="button-global button-border small autoHeight mr-2 mb-1" onClick={() => this.handleUnrelease(record)} />
                        )}
                        {record.Status === 'Released' && !record.IsActive && (
                            <ButtonComponent type="button" value="Re-release" className="button-global button-primary small autoHeight mr-2 mb-1" onClick={() => this.handleRelease(record)} />
                        )}
                        {record.Status !== 'Archived' && (
                            <ButtonComponent type="button" value="Archive" className="button-global button-danger small autoHeight mb-1" onClick={() => this.onArchiveClick(record)} />
                        )}
                    </>
                )
            },
        ];

        return (
            <div className="container-fluid p-3">
                <PageHeader
                    title="Interview Scheduling"
                    breadcrumbItems={[{ label: 'Dashboard', path: routesAdmin.dashboard.path }, { label: 'Candidates' }, { label: 'Interview Scheduling' }]}
                />
                <div className="max1200 m-auto">
                    <Card>
                        <h5 className="mb-3">Import Interview Schedule</h5>
                        <div className="row">
                            <div className="col-sm-6 mb-2">
                                <DropdownList
                                    value={importPostId || 0}
                                    name="importPostId"
                                    onChange={this.onImportPostChange}
                                    placeholder="Select Post"
                                    heading="Post/Exam"
                                    isRequired={true}
                                    options={posts}
                                />
                            </div>
                        </div>
                        {importPostId && (
                            <>
                                <a className="button-global button-border addbutton autoWidth d-inline-block text-center autoHeight mb-3 d-flex align-items-center justify-content-center"
                                    href={require('../../assets/template/StudentInterviewTimeData.xlsx')} download="InterviewScheduleTemplate.xlsx">
                                    Download Template (RoleNumber, InterviewDate, InterviewTime, Post, PostId — optional InterviewReleasedDate)
                                </a>
                                <div className="mb-2 col-sm-12 uploadCard">
                                    <FileUpload onFileDrop={this.onFileDrop} onError={this.onFileError} />
                                    {fileError && <label className="textAlignCenter"><span className="color-red">Error: {fileError}</span></label>}
                                </div>
                                <ButtonComponent
                                    type="button"
                                    value={validating ? 'Validating…' : 'Validate'}
                                    onClick={this.handleValidate}
                                    className="button-global button-primary autoHeight small"
                                    disabled={validating || !this.state.selectedFile}
                                />
                                {this.renderValidationSummary()}
                            </>
                        )}
                    </Card>

                    <Card className="mt-3">
                        <h5 className="mb-3">Interview Schedules</h5>
                        <div className="row mb-2">
                            <div className="col-sm-4 mb-2">
                                <DropdownList
                                    value={this.state.filterPostId || 0}
                                    name="filterPostId"
                                    onChange={this.onFilterPostChange}
                                    placeholder="All Posts"
                                    heading="Filter by Post"
                                    options={posts}
                                />
                            </div>
                            <div className="col-sm-4 mb-2">
                                <DropdownList
                                    value={this.state.filterStatus || ''}
                                    name="filterStatus"
                                    onChange={this.onFilterStatusChange}
                                    placeholder="All Statuses"
                                    heading="Filter by Status"
                                    options={STATUS_OPTIONS.map(s => ({ Id: s, Name: s }))}
                                />
                            </div>
                            <div className="col-sm-4 mb-2">
                                <label className="card-holder-name">Filter by Interview Date</label>
                                <DatePicker allowClear format="DD-MMM-YYYY" style={{ width: '100%' }}
                                    value={this.state.filterDate} onChange={this.onFilterDateChange} />
                            </div>
                        </div>
                        <Table size='small'
                            bordered
                            rowKey='Id'
                            columns={TableColumns}
                            dataSource={schedules}
                            pagination={this.state.pagination}
                            onChange={this.onTableAction}
                            footer={() => <label style={{ textAlign: 'right', marginRight: '20px' }}>Total Records: {schedules.length}</label>}
                            className="table-striped-rows admin-table"
                            locale={{ emptyText: <EmptyState icon="calendar" title="No Interview Schedules Found" description="No interview schedules match the current filters." /> }}
                        />
                    </Card>

                    {this.renderCallLetterSection()}
                </div>

                <Modal
                    title="Preview Interview Call Letter"
                    visible={this.state.showPreviewModal}
                    onCancel={this.closePreviewModal}
                    footer={null}
                    width={800}
                    className="modelPopAnt admin-home"
                    destroyOnClose={true}
                >
                    {this.state.previewLoading && <LoadingSpinner />}
                    {!this.state.previewLoading && this.state.previewData && (
                        <>
                            <p style={{ fontSize: 12, color: '#888' }} className="mb-2">
                                This is a preview only — no Call Letter has been released. Sample data is used for any
                                candidate whose Post has no released schedule yet.
                            </p>
                            <div style={{ border: '1px solid #eee', padding: 16, background: '#fff', maxHeight: 500, overflowY: 'auto' }}
                                dangerouslySetInnerHTML={{ __html: this.state.previewData.MergedHtml }} />
                        </>
                    )}
                </Modal>

                <Modal
                    title="Edit Interview Schedule"
                    visible={this.state.showEditModal}
                    onCancel={this.closeEditModal}
                    footer={null}
                    width={480}
                    className="modelPopAnt admin-home"
                    destroyOnClose={true}
                >
                    {this.state.editing && (
                        <Card>
                            <p className="mb-2"><b>Roll Number:</b> {this.state.editing.RollNumber} ({this.state.editing.StudentName})</p>
                            <div className="mb-2">
                                <label className="card-holder-name">Interview Date<span className="color-red">*</span></label>
                                <DatePicker format="DD-MMM-YYYY" style={{ width: '100%' }}
                                    value={this.state.editInterviewDate}
                                    onChange={(d) => this.setState({ editInterviewDate: d })} />
                            </div>
                            <div className="mb-2">
                                <label className="card-holder-name">Interview Time<span className="color-red">*</span></label>
                                <input type="text" className="form-control" value={this.state.editInterviewTime}
                                    onChange={(e) => this.setState({ editInterviewTime: e.target.value })} placeholder="e.g. 09:00 AM to 10:30 AM" />
                            </div>
                            <div className="mb-2">
                                <label className="card-holder-name">Venue (optional)</label>
                                <input type="text" className="form-control" value={this.state.editVenue}
                                    onChange={(e) => this.setState({ editVenue: e.target.value })} />
                            </div>
                            <div className="pt-2 buttonAlignCenter">
                                <ButtonComponent type="button" value="Cancel" onClick={this.closeEditModal} className="button-global button-secondary mr-4 autoHeight small" />
                                <ButtonComponent type="button" value={this.state.saving ? "Saving…" : "Save"} onClick={this.handleSaveEdit} className="button-global button-primary autoHeight small" disabled={this.state.saving} />
                            </div>
                        </Card>
                    )}
                </Modal>

                <Modal
                    centered={true}
                    width={400}
                    className="modelPopAnt admin-home"
                    visible={this.state.showArchiveConfirm}
                    onCancel={() => this.setState({ showArchiveConfirm: false, archiveTargetId: null })}
                    footer={false}
                >
                    <ConfirmPopup
                        onCancel={() => this.setState({ showArchiveConfirm: false, archiveTargetId: null })}
                        onOk={this.onConfirmArchive}
                        message="Are you sure you want to archive this interview schedule? It will no longer be visible to the candidate. The record is retained for audit, not deleted."
                    />
                </Modal>
            </div>
        )
    };
}

export default InterviewScheduleMaster_new;
